import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Car, Users, Clock, Shield, ChevronRight, Sparkles, 
  QrCode, MapPin, Star, CreditCard, Phone, Bell, 
  CheckCircle, ArrowRight, Zap, BarChart3
} from "lucide-react";
import { useAuth } from "@/App";
import { useEffect, useState } from "react";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "valet_staff") navigate("/valet");
      else navigate("/dashboard");
    }
  }, [user, navigate]);

  // Auto-rotate how it works steps
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    { icon: <Zap className="w-6 h-6" />, title: "One-Tap Request", desc: "Guests tap a button when asking for the bill. Car arrives by the time they exit." },
    { icon: <QrCode className="w-6 h-6" />, title: "QR Code Check-in", desc: "Scan a venue QR code on arrival. No app download needed, works instantly." },
    { icon: <Clock className="w-6 h-6" />, title: "Real-Time Tracking", desc: "Live status updates via WebSocket. Know exactly when your car is being fetched." },
    { icon: <MapPin className="w-6 h-6" />, title: "Smart Parking Map", desc: "Valet staff see a live parking grid. Find and retrieve cars in under 2 minutes." },
    { icon: <Shield className="w-6 h-6" />, title: "Liability Waivers", desc: "Digital waivers with accept/decline. Full legal compliance built right in." },
    { icon: <Phone className="w-6 h-6" />, title: "Privacy First", desc: "Guest phone numbers are masked. Staff communicates via in-app notifications." },
    { icon: <CreditCard className="w-6 h-6" />, title: "Stripe Payments", desc: "Driver service packages with secure Stripe checkout. Revenue tracking included." },
    { icon: <BarChart3 className="w-6 h-6" />, title: "Analytics Dashboard", desc: "Peak hours, avg wait times, revenue, and ratings — all in one admin panel." }
  ];

  const howItWorks = [
    { step: "01", title: "Guest Arrives", desc: "Guest scans QR code or valet registers them as a walk-in. Car is parked with a digital token.", icon: <QrCode className="w-8 h-8" /> },
    { step: "02", title: "Guest Requests Car", desc: "One tap on the app, or valet staff initiates retrieval when the bill is requested.", icon: <Bell className="w-8 h-8" /> },
    { step: "03", title: "Real-Time Fetch", desc: "Valet sees the request instantly, locates the car on the parking map, and starts fetching.", icon: <Car className="w-8 h-8" /> },
    { step: "04", title: "Car Ready", desc: "Guest gets a notification. Car is waiting at the exit. Under 2 minutes, every time.", icon: <CheckCircle className="w-8 h-8" /> }
  ];

  const stats = [
    { number: "< 2", unit: "min", label: "Average Wait" },
    { number: "3", unit: "", label: "Dashboard Roles" },
    { number: "24/7", unit: "", label: "Real-Time Updates" },
    { number: "100%", unit: "", label: "Privacy Protected" }
  ];

  const testimonials = [
    { name: "Rahul Mehta", role: "Restaurant Owner, Mumbai", text: "VeloPark transformed our guest experience. Cars are ready before they even reach the exit. The QR system is brilliant.", rating: 5 },
    { name: "Priya Sharma", role: "Event Planner", text: "Managing 200+ cars at an event used to be chaos. VeloPark's parking map and walk-in system made it seamless.", rating: 5 },
    { name: "James Wilson", role: "Hotel GM, Dubai", text: "The privacy features are a game-changer. Our VIP guests love that their details stay protected from staff.", rating: 5 }
  ];

  return (
    <div className="min-h-screen bg-[#090A0F] relative overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-screen">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2YWxldCUyMHBhcmtpbmclMjBzZXJ2aWNlJTIwbmlnaHR8ZW58MHx8fHwxNzc5ODg0NDU4fDA&ixlib=rb-4.1.0&q=85')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0F]/80 via-[#090A0F]/60 to-[#090A0F]" />
        
        {/* Header */}
        <header className="relative z-50 crystal-header sticky top-0">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/logo.svg" alt="VeloPark" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full" />
              <span className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">VeloPark</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Features</a>
              <a href="#how-it-works" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">How It Works</a>
              <a href="#testimonials" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Reviews</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" className="text-white hover:text-[#D4AF37] hover:bg-transparent text-sm px-2 sm:px-4" onClick={() => navigate("/login")} data-testid="login-btn">
                Sign In
              </Button>
              <Button className="btn-primary rounded-full px-4 sm:px-6 text-sm" onClick={() => navigate("/register")} data-testid="get-started-btn">
                Get Started
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-20 sm:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full glass-card mb-6 sm:mb-8">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs sm:text-sm text-[#A1A1AA]">Premium Valet & Driver Services</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-semibold text-white leading-tight tracking-tight mb-4 sm:mb-6">
              Your Car, Ready
              <span className="block text-[#D4AF37]">Before You Are</span>
            </h1>
            
            <p className="text-base sm:text-lg text-[#A1A1AA] max-w-xl mb-8 sm:mb-10 leading-relaxed">
              The complete valet management platform for restaurants and events. Guests request their car with one tap — it's waiting by the time they exit. Under 2 minutes, every time.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button className="btn-primary text-base sm:text-lg px-8 py-6 rounded-full flex items-center gap-2" onClick={() => navigate("/register")} data-testid="hero-get-started-btn">
                Start Free Trial <ChevronRight className="w-5 h-5" />
              </Button>
              <Button variant="outline" className="btn-secondary text-base sm:text-lg px-8 py-6 rounded-full" onClick={() => navigate("/login")} data-testid="hero-signin-btn">
                Sign In
              </Button>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6" data-testid="stats-bar">
              {stats.map((stat, i) => (
                <div key={i} className="text-center sm:text-left">
                  <p className="text-2xl sm:text-3xl font-bold text-white">
                    {stat.number}<span className="text-[#D4AF37] text-lg">{stat.unit}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-[#A1A1AA]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="relative z-10 py-16 sm:py-24 grid-pattern">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-3 block">Platform Features</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">
              Everything You Need to Run a World-Class Valet
            </h2>
            <p className="text-[#A1A1AA] text-base sm:text-lg max-w-2xl mx-auto">
              From QR check-in to real-time analytics — VeloPark handles it all.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="glass-card rounded-2xl p-6 sm:p-8 hover-lift group" data-testid={`feature-card-${index}`}>
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-5 group-hover:bg-[#D4AF37]/20 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-base sm:text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-16 sm:py-24 bg-[#0D0E14]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-3 block">Simple Process</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">
              How VeloPark Works
            </h2>
            <p className="text-[#A1A1AA] text-base sm:text-lg max-w-2xl mx-auto">
              Four simple steps from arrival to departure. Zero hassle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6">
            {howItWorks.map((item, index) => (
              <div 
                key={index} 
                className={`relative rounded-2xl p-6 sm:p-8 transition-all duration-500 cursor-pointer ${
                  activeStep === index 
                    ? "bg-[#D4AF37]/10 border border-[#D4AF37]/30 ring-1 ring-[#D4AF37]/20" 
                    : "glass-card hover:border-white/20"
                }`}
                onClick={() => setActiveStep(index)}
                data-testid={`step-${index}`}
              >
                <span className={`text-4xl sm:text-5xl font-bold mb-4 block ${activeStep === index ? 'text-[#D4AF37]' : 'text-white/10'}`}>
                  {item.step}
                </span>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${activeStep === index ? 'bg-[#D4AF37] text-[#090A0F]' : 'bg-white/5 text-[#D4AF37]'}`}>
                  {item.icon}
                </div>
                <h3 className="font-semibold text-lg text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{item.desc}</p>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute -right-5 top-1/2 -translate-y-1/2 w-6 h-6 text-white/10 z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Dashboards Section */}
      <section className="relative z-10 py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-3 block">Built for Everyone</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">
              Three Powerful Dashboards
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Admin Dashboard",
                desc: "Full venue control, staff management, analytics, revenue tracking, ratings, and multi-venue overview.",
                items: ["Venue & Staff Management", "Analytics & Reports", "Revenue & Rating Insights", "Walk-in Guest Management"],
                color: "from-[#D4AF37]/20 to-transparent",
                icon: <BarChart3 className="w-6 h-6" />
              },
              {
                title: "Valet Staff Dashboard",
                desc: "Real-time car tracking, parking map, walk-in registration, and waiver management on the floor.",
                items: ["Live Request Feed", "Parking Spot Map", "Walk-in Registration", "Privacy-Masked Contacts"],
                color: "from-emerald-500/20 to-transparent",
                icon: <Car className="w-6 h-6" />
              },
              {
                title: "Customer Dashboard",
                desc: "One-tap car request, driver booking with Stripe payment, real-time status, and service ratings.",
                items: ["One-Tap Car Request", "Book & Pay for Drivers", "Live Status Tracking", "Rate Your Experience"],
                color: "from-blue-500/20 to-transparent",
                icon: <Users className="w-6 h-6" />
              }
            ].map((dash, i) => (
              <div key={i} className={`glass-card rounded-2xl overflow-hidden`} data-testid={`dashboard-card-${i}`}>
                <div className={`h-2 bg-gradient-to-r ${dash.color}`} />
                <div className="p-6 sm:p-8">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-5">
                    {dash.icon}
                  </div>
                  <h3 className="font-semibold text-xl text-white mb-3">{dash.title}</h3>
                  <p className="text-sm text-[#A1A1AA] mb-5">{dash.desc}</p>
                  <ul className="space-y-2.5">
                    {dash.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-white">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Venue Types */}
      <section className="relative z-10 py-16 sm:py-24 bg-[#0D0E14]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4 block">For Every Occasion</span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-6">
                Restaurants, Events & Beyond
              </h2>
              <p className="text-[#A1A1AA] text-base sm:text-lg mb-8 leading-relaxed">
                Whether it's a Michelin-star dinner or a grand event, VeloPark ensures every guest gets a white-glove parking experience.
              </p>
              <ul className="space-y-4">
                {[
                  { name: "Fine Dining Restaurants", desc: "QR codes on tables, instant valet at checkout" },
                  { name: "Event Venues", desc: "Bulk walk-in registration, 200+ cars managed seamlessly" },
                  { name: "Corporate Events", desc: "Driver services, VIP handling, analytics reporting" },
                  { name: "Hotels & Resorts", desc: "Multi-venue management, 24/7 valet operations" }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                    <div>
                      <span className="text-white font-medium">{item.name}</span>
                      <p className="text-sm text-[#A1A1AA]">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <img src="https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2YWxldCUyMHBhcmtpbmclMjBzZXJ2aWNlJTIwbmlnaHR8ZW58MHx8fHwxNzc5ODg0NDU4fDA&ixlib=rb-4.1.0&q=85" alt="Luxury Car" className="rounded-2xl w-full h-40 sm:h-48 object-cover" />
              <img src="https://images.unsplash.com/photo-1758285477206-e240db4c7aa5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwZmluZSUyMGRpbmluZyUyMGVudHJhbmNlfGVufDB8fHx8MTc3OTg4NDQ2MHww&ixlib=rb-4.1.0&q=85" alt="Restaurant" className="rounded-2xl w-full h-40 sm:h-48 object-cover mt-6 sm:mt-8" />
              <img src="https://images.unsplash.com/photo-1592514313074-794923c98162?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHx3ZWRkaW5nJTIwdmVudWUlMjBsdXh1cnklMjBjYXIlMjBlbnRyYW5jZXxlbnwwfHx8fDE3Nzk4ODQ0NTh8MA&ixlib=rb-4.1.0&q=85" alt="Event Venue" className="rounded-2xl w-full h-40 sm:h-48 object-cover" />
              <img src="https://images.pexels.com/photos/19082343/pexels-photo-19082343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Venue" className="rounded-2xl w-full h-40 sm:h-48 object-cover mt-6 sm:mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="relative z-10 py-16 sm:py-24 bg-[#0D0E14]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-3 block">Testimonials</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">
              Loved by Venues & Guests
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <div key={i} className="glass-card rounded-2xl p-6 sm:p-8" data-testid={`testimonial-${i}`}>
                <div className="flex mb-4">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-4 h-4 ${s <= t.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">"{t.text}"</p>
                <div>
                  <p className="text-white font-medium text-sm">{t.name}</p>
                  <p className="text-xs text-[#A1A1AA]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="glass-card rounded-2xl sm:rounded-3xl p-8 sm:p-16 text-center max-w-3xl mx-auto relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent" />
            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">
                Ready to Upgrade Your Valet Service?
              </h2>
              <p className="text-[#A1A1AA] text-base sm:text-lg mb-8 max-w-xl mx-auto">
                Join VeloPark today. Set up your first venue in minutes. Start delivering a premium experience your guests will remember.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="btn-primary text-base sm:text-lg px-10 py-6 rounded-full" onClick={() => navigate("/register")} data-testid="cta-get-started-btn">
                  Create Your Account <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.svg" alt="VeloPark" className="w-8 h-8 rounded-full" />
                <span className="font-display text-lg text-white">VeloPark</span>
              </div>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                Premium valet and driver services for restaurants, events, and luxury venues.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-[#A1A1AA]">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-[#A1A1AA]">
                <li><a href="https://velopark.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">velopark.in</a></li>
                <li><a href="mailto:contact@velopark.in" className="hover:text-white transition-colors">contact@velopark.in</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Get Started</h4>
              <ul className="space-y-2 text-sm text-[#A1A1AA]">
                <li><button onClick={() => navigate("/register")} className="hover:text-white transition-colors">Create Account</button></li>
                <li><button onClick={() => navigate("/login")} className="hover:text-white transition-colors">Sign In</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[#A1A1AA] text-xs">&copy; 2026 VeloPark. Premium Valet Services. All rights reserved.</p>
            <div className="flex items-center gap-4 text-xs text-[#A1A1AA]">
              <a href="https://velopark.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">velopark.in</a>
              <span className="text-white/20">|</span>
              <a href="mailto:contact@velopark.in" className="hover:text-white transition-colors">contact@velopark.in</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
